import { getApiErrorMessage } from '../../utils/apiError';
import { WZ_SERVER_ERROR_KEYS } from '../../utils/wzServerErrorKeys';
import { getBlockedActionMessage, normalizeRelatedDocuments } from './blockedActions';
import { formatWarehouseQuantity } from './warehouseQuantityConversion';

/**
 * Single home for turning a WZ API failure into a Polish sentence an operator
 * can act on. Sibling of `pzErrorMessages.ts`.
 *
 * Two layers, both needed:
 *  - `WZ_ERROR_CODE_KEYS` maps the backend's stable `code` values (release
 *    guards, status-transition guards, number collisions) to operator wording;
 *  - `WZ_SERVER_ERROR_KEYS` (utils/wzServerErrorKeys.ts) maps the raw English
 *    express-validator strings, and is applied on the fallback path so a
 *    request-shape rejection still lands in Polish.
 *
 * Before this module, the same backend rejection had three different fates: a
 * precise sentence in `WZDetail`'s inline ternary chain, a Polish-but-different
 * one in `WZDocuments`, and raw English in `WZNew`. Route every WZ error here.
 *
 * NOT handled here: `WZ_ORDER_FULLY_COVERED`, which needs the blocking document
 * numbers interpolated plus a confirm action — see `wzOrderFullyCoveredMessage.ts`.
 * `WZ_RELEASE_EXCEEDS_ORDERED_QUANTITY` is likewise a confirmation flow, not a
 * toast: it is a warning the operator answers, handled in `WZDetail`.
 */
export const WZ_ERROR_CODE_KEYS: Record<string, string> = {
  WZ_ORDER_NOT_FOUND: 'wz:error.orderNotFound',
  // Blocked-action codes (services/warehouseBlockedActionsService.js): the same
  // key renders the disabled-button tooltip, the card alert and the toast.
  // WZ_DELETE_BLOCKED_STATUS keeps its status-interpolated sentence in
  // WZDocuments when no related documents are attached.
  WZ_DELETE_BLOCKED_STATUS: 'wz:error.deleteBlockedReleased',
  WZ_CANCEL_BLOCKED_RELEASED: 'wz:error.cancelBlockedReleased',
  WZ_RELEASE_BLOCKED_CANCELLED: 'wz:error.releaseBlockedCancelled',
  WZ_NUMBER_EXISTS: 'wz:error.wzNumberExists',
  WZ_QUANTITY_INVALID: 'wz:serverErrors.quantityPrecision',
  // Inconsistent converter or sale pair (wzService): points at „Zmień przelicznik”.
  WZ_QUANTITY_PAIR_INCONSISTENT: 'wz:error.quantityPairInconsistent',
  WZ_CORRECTION_REASON_REQUIRED: 'wz:error.correctionReasonRequired',
  WZ_CORRECTION_STALE: 'wz:error.correctionStale',
  WZ_CORRECTION_ITEMS_REQUIRED: 'wz:error.correctionItemsRequired',
  WZ_CORRECTION_TRACE_REQUIRED: 'wz:error.correctionTraceRequired',
  WZ_CORRECTION_QUANTITY_MISMATCH: 'wz:error.correctionQuantityMismatch',
  WZ_CORRECTION_ORDER_MISSING: 'wz:error.correctionOrderMissing',
  WZ_UPDATE_BLOCKED_RELEASED: 'wz:error.updateBlockedReleased',
  WZ_UPDATE_BLOCKED_RECEIVED: 'wz:error.updateBlockedReceived',
  WZ_INVALID_STATUS_TRANSITION: 'wz:error.invalidStatusTransition',
  // Release date given at release (owner decision 2026-10-14).
  WZ_RELEASE_DATE_IN_FUTURE: 'wz:error.releaseDateInFuture',
  WZ_RELEASE_DATE_INVALID: 'wz:error.releaseDateInvalid',
  WZ_RELEASE_ITEMS_NOT_LINKED_TO_INVENTORY: 'wz:error.itemsNotLinkedToInventory',
  INCOMPATIBLE_INVENTORY_UNIT: 'wz:error.incompatibleInventoryUnit',
  // Legacy truck source (decision 2026-09-23-wz-source-picker), kept for WZ
  // saved before the warehouse-only decision of 2026-10-04.
  WZ_SOURCE_INVALID: 'wz:source.errors.invalid',
  WZ_SOURCE_DELIVERY_NOT_FOUND: 'wz:source.errors.deliveryUnavailable',
  // Warehouse only and „Wybiorę partie” (decision 2026-10-04).
  WZ_SOURCE_WAREHOUSE_ONLY: 'wz:source.errors.warehouseOnly',
  WZ_BATCH_PICK_INVALID: 'wz:source.errors.batchPickInvalid',
  // WZ_BASE_UNIT_MISMATCH remains a blocking code below. Its descriptive
  // backend prose is intentionally not rendered: UI copy must use a stable
  // Polish i18n key rather than an API message.
};

/**
 * Codes describing a state the operator has to change (or reload) before the
 * action can ever succeed. Their toasts stay on screen until dismissed — a
 * retry without reading them will simply fail the same way.
 */
const WZ_BLOCKING_ERROR_CODES = new Set([
  'WZ_ORDER_NOT_FOUND',
  'WZ_UPDATE_BLOCKED_RELEASED',
  'WZ_UPDATE_BLOCKED_RECEIVED',
  'WZ_INVALID_STATUS_TRANSITION',
  'WZ_RELEASE_ITEMS_NOT_LINKED_TO_INVENTORY',
  'WZ_BASE_UNIT_MISMATCH',
  'INSUFFICIENT_INVENTORY',
  'INCOMPATIBLE_INVENTORY_UNIT',
  'WZ_DELETE_BLOCKED_STATUS',
  'WZ_CANCEL_BLOCKED_RELEASED',
  'WZ_RELEASE_BLOCKED_CANCELLED',
  'WZ_SOURCE_EXCEEDS_DELIVERY',
  'WZ_SOURCE_DELIVERY_NOT_FOUND',
  'WZ_SOURCE_WAREHOUSE_ONLY',
  'WZ_BATCH_PICK_EXCEEDS_AVAILABLE',
]);

export function isBlockingWZError(error: any): boolean {
  const code = error?.response?.data?.code;
  return typeof code === 'string' && WZ_BLOCKING_ERROR_CODES.has(code);
}

/**
 * `INSUFFICIENT_INVENTORY` carries numbers, so it cannot live in the static code
 * map. Split by cause, because the two states need different operator actions:
 * nothing on stock at all means the PZ was never received, while a shortfall
 * means the release has to be trimmed.
 */
function getInsufficientInventoryMessage(t: any, data: any): string {
  return Number(data?.available) <= 0
    ? t('wz:error.virtualReceiptMissing')
    : t('wz:error.insufficientInventory', {
      needed: data?.needed,
      available: data?.available,
    });
}

/**
 * @param fallbackKey i18n key used when the server says nothing specific. Must
 *   name the failed operation, e.g. `wz:error.statusUpdateFailed`.
 * @param fallbackParams interpolation for the fallback key — pass the document
 *   number so even the generic path identifies the object.
 */
export function getWZErrorMessage(
  error: any,
  t: any,
  fallbackKey: string,
  fallbackParams?: Record<string, unknown>
): string {
  const data = error?.response?.data;
  const code = data?.code;

  if (code === 'INSUFFICIENT_INVENTORY') {
    return getInsufficientInventoryMessage(t, data);
  }
  if (code === 'WZ_SOURCE_EXCEEDS_DELIVERY') {
    return t('wz:source.errors.exceedsDeliveryNamed', {
      product: data?.productName || '',
      available: formatWarehouseQuantity(Number(data?.available) || 0, data?.unit || 'pcs'),
    });
  }
  if (code === 'WZ_BATCH_PICK_EXCEEDS_AVAILABLE') {
    return data?.pzNumber && data?.available !== null && data?.available !== undefined
      ? t('wz:source.errors.batchPickExceedsNamed', {
        product: data?.productName || '',
        pzNumber: data.pzNumber,
        available: formatWarehouseQuantity(Number(data.available) || 0, data?.unit || 'pcs'),
      })
      : t('wz:source.errors.batchPickExceeds');
  }
  if (typeof code === 'string' && WZ_ERROR_CODE_KEYS[code]) {
    // A rejection that names the PZ batches the WZ consumed reads exactly like
    // the proactive card alert.
    if (Array.isArray(data?.relatedDocuments) && data.relatedDocuments.length > 0) {
      return getBlockedActionMessage(
        { code, relatedDocuments: normalizeRelatedDocuments(data.relatedDocuments) },
        t,
        'wz',
        WZ_ERROR_CODE_KEYS,
      );
    }
    return t(WZ_ERROR_CODE_KEYS[code]);
  }
  return getApiErrorMessage(error, t, fallbackKey, WZ_SERVER_ERROR_KEYS, fallbackParams);
}
